import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import {
    API, ROUTES, findStatut, findType, findEtape, findGravite, findTypeJournal, unwrap, apiMessage, toast,
    formatAmount, formatDate, TypeBadge, StatutBadge, ProgressBar,
} from "./projetsCommon";
import "./GestionProjets.css";

// Montants compacts pour les indicateurs : 1 250 000 → « 1,25 M DH »
const compact = (v) => {
    const n = Number(v || 0);
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `${(n / 1_000_000).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} M DH`;
    if (abs >= 10_000) return `${(n / 1_000).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} k DH`;
    return formatAmount(n);
};
const moisLabel = (m) => {
    const [y, mo] = m.split("-").map(Number);
    return new Date(y, mo - 1, 1).toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });
};

// ---------- Briques ----------
const Kpi = ({ icon, tone, label, value, sub, to }) => {
    const body = (
        <div className={`gp-kpi2 gp-kpi2-${tone || "neutral"}`}>
            <div className="gp-kpi2-head"><i className={`fas ${icon} mr-1`}></i>{label}</div>
            <div className="gp-kpi2-value">{value}</div>
            {sub && <div className="gp-kpi2-sub">{sub}</div>}
        </div>
    );
    return <div className="col-6 col-lg-3 mb-3">{to ? <Link to={to} className="gp-kpi2-link">{body}</Link> : body}</div>;
};

const Card = ({ title, icon, right, children, bodyClass = "card-body" }) => (
    <div className="card h-100 mb-0">
        <div className="card-header py-2 d-flex align-items-center">
            {icon && <i className={`fas ${icon} text-muted mr-2`}></i>}<strong>{title}</strong>
            {right && <div className="ml-auto small">{right}</div>}
        </div>
        <div className={bodyClass}>{children}</div>
    </div>
);

const Empty = ({ children }) => <p className="text-muted small mb-0">{children}</p>;

// Barres horizontales de répartition (une seule couleur par ligne : identité portée par le libellé)
const Repartition = ({ items, find }) => {
    const total = items.reduce((s, i) => s + i.nombre, 0) || 1;
    if (items.length === 0) return <Empty>Aucun projet</Empty>;
    return items.slice().sort((a, b) => b.nombre - a.nombre).map((i) => {
        const meta = find(i.cle);
        return (
            <div key={i.cle} className="mb-2" title={`${meta.label} : ${i.nombre} projet(s)`}>
                <div className="d-flex justify-content-between small"><span>{meta.label}</span><strong>{i.nombre}</strong></div>
                <div className="gp-hbar"><div style={{ width: `${(i.nombre / total) * 100}%`, backgroundColor: meta.color }}></div></div>
            </div>
        );
    });
};

// Évolution mensuelle : barres groupées CA / charges (même unité, un seul axe)
const Evolution = ({ data }) => {
    const max = Math.max(1, ...data.flatMap((m) => [m.chiffreAffaires, m.charges]));
    const vide = data.every((m) => !m.chiffreAffaires && !m.charges);
    return (
        <>
            <div className="gp-legend mb-2">
                <span><i className="gp-swatch gp-c1"></i>Chiffre d'affaires</span>
                <span><i className="gp-swatch gp-c2"></i>Charges</span>
            </div>
            {vide ? <Empty>Aucune facture ni dépense imputée aux projets sur les 6 derniers mois.</Empty> : (
                <div className="gp-vbars">
                    {data.map((m) => (
                        <div key={m.mois} className="gp-vbar-group">
                            <div className="gp-vbar-pair">
                                <div className="gp-vbar gp-c1" style={{ height: `${(Math.max(0, m.chiffreAffaires) / max) * 100}%` }}
                                    title={`${moisLabel(m.mois)} — CA : ${formatAmount(m.chiffreAffaires)}`}></div>
                                <div className="gp-vbar gp-c2" style={{ height: `${(Math.max(0, m.charges) / max) * 100}%` }}
                                    title={`${moisLabel(m.mois)} — Charges : ${formatAmount(m.charges)}`}></div>
                            </div>
                            <div className="gp-vbar-label">{moisLabel(m.mois)}</div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

// Jauge budget / consommé + engagé
const Jauge = ({ budget, charges, engage }) => {
    if (!budget) return <Empty>Aucun budget renseigné sur les projets ouverts.</Empty>;
    const pctC = Math.min(100, (charges / budget) * 100);
    const pctE = Math.min(100 - pctC, (engage / budget) * 100);
    const total = ((charges + engage) / budget) * 100;
    return (
        <>
            <div className="d-flex justify-content-between align-items-end mb-1">
                <div><div className="gp-info-label">Consommé + engagé</div><div className="h4 mb-0">{total.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} %</div></div>
                <div className="text-right small text-muted">Budget<br /><strong className="text-body">{compact(budget)}</strong></div>
            </div>
            <div className="gp-stack">
                <div className="gp-c2" style={{ width: `${pctC}%` }} title={`Charges réalisées : ${formatAmount(charges)}`}></div>
                <div className="gp-c2-light" style={{ width: `${pctE}%` }} title={`Engagé : ${formatAmount(engage)}`}></div>
            </div>
            <div className="gp-legend mt-2">
                <span><i className="gp-swatch gp-c2"></i>Réalisé {compact(charges)}</span>
                <span><i className="gp-swatch gp-c2-light"></i>Engagé {compact(engage)}</span>
            </div>
            <div className={`mt-2 small ${total > 100 ? "text-danger font-weight-bold" : "text-muted"}`}>
                {total > 100
                    ? <><i className="fas fa-exclamation-triangle mr-1"></i>Dépassement global : {compact(charges + engage - budget)}</>
                    : <>Reste disponible : {compact(budget - charges - engage)}</>}
            </div>
        </>
    );
};

const ETAPES_ORDRE = ["Prospection", "Offre", "Negociation", "Gagne", "Perdu"];

const Pipeline = ({ etapes }) => {
    const list = ETAPES_ORDRE.map((e) => etapes.find((x) => x.etape === e) || { etape: e, nombre: 0, montant: 0, montantPondere: 0 });
    const max = Math.max(1, ...list.map((e) => e.montant));
    if (list.every((e) => e.nombre === 0)) return <Empty>Aucun projet de type Vente avec une étape renseignée.</Empty>;
    return list.map((e) => (
        <div key={e.etape} className="mb-2" title={`${findEtape(e.etape)?.label} : ${e.nombre} projet(s) — ${formatAmount(e.montant)} (pondéré ${formatAmount(e.montantPondere)})`}>
            <div className="d-flex justify-content-between small">
                <span>{findEtape(e.etape)?.label} <span className="text-muted">({e.nombre})</span></span>
                <strong>{compact(e.montant)}</strong>
            </div>
            <div className="gp-hbar"><div className={e.etape === "Perdu" ? "gp-muted-fill" : "gp-c1"} style={{ width: `${(e.montant / max) * 100}%` }}></div></div>
        </div>
    ));
};

// ---------- Page ----------
const DashboardProjets = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [financeFilter, setFinanceFilter] = useState("ouverts");

    const load = () => {
        setError(false);
        axiosInstance.get(API.dashboardComplet)
            .then((res) => setData(unwrap(res)))
            .catch((e) => { setError(true); toast("error", apiMessage(e, "Impossible de charger le tableau de bord")); });
    };
    useEffect(() => { load(); }, []);

    const finances = useMemo(() => {
        if (!data) return [];
        const list = data.financesProjets.filter((p) => {
            const s = findStatut(p.statut).value;
            if (financeFilter === "ouverts") return s !== 2 && s !== 6;
            if (financeFilter === "alerte") return p.horsBudget || p.marge < 0;
            return true;
        });
        return list.slice(0, 10);
    }, [data, financeFilter]);

    if (error) return (
        <div className="container-fluid mt-4 text-center py-5">
            <p className="text-muted">Le tableau de bord n'a pas pu être chargé.</p>
            <button className="btn btn-outline-primary" onClick={load}>Réessayer</button>
        </div>
    );
    if (!data) return <div className="container-fluid mt-4"><p>Chargement...</p></div>;

    const k = data.kpi;

    return (
        <div className="container-fluid mt-4 gp-dashboard">
            <div className="d-flex justify-content-between align-items-center flex-wrap my-3">
                <h3 className="mb-2">Tableau de bord des projets</h3>
                <div className="mb-2">
                    <button className="btn btn-light mr-2" onClick={load} title="Actualiser"><i className="fas fa-sync-alt"></i></button>
                    <Link to={ROUTES.liste} className="btn btn-outline-primary mr-2"><i className="fas fa-list mr-1"></i>Projets</Link>
                    <Link to={ROUTES.taches} className="btn btn-outline-primary mr-2"><i className="fas fa-tasks mr-1"></i>Tâches</Link>
                    <Link to={ROUTES.employes} className="btn btn-outline-secondary"><i className="fas fa-users mr-1"></i>Employés</Link>
                </div>
            </div>

            {/* ---- Indicateurs : activité ---- */}
            <div className="row">
                <Kpi icon="fa-project-diagram" label="Projets ouverts" value={k.projetsOuverts}
                    sub={`${k.projetsEnCours} en cours · ${k.projetsTotal} au total`} to={ROUTES.liste} />
                <Kpi icon="fa-clock" tone={k.projetsEnRetard ? "critical" : "good"} label="Projets en retard" value={k.projetsEnRetard}
                    sub={k.projetsHorsBudget ? `${k.projetsHorsBudget} hors budget` : "aucun hors budget"} />
                <Kpi icon="fa-tasks" tone={k.tachesEnRetard ? "warning" : "neutral"} label="Tâches ouvertes" value={k.tachesOuvertes}
                    sub={`${k.tachesEnRetard} en retard · ${k.tachesBloquees} bloquée(s)`} to={ROUTES.taches} />
                <Kpi icon="fa-exclamation-triangle" tone={k.incidentsOuverts ? "critical" : "good"} label="Incidents ouverts" value={k.incidentsOuverts}
                    sub={k.incidentsOuverts ? "voir la liste ci-dessous" : "rien à signaler"} />
            </div>

            {/* ---- Indicateurs : finances ---- */}
            <div className="row">
                <Kpi icon="fa-arrow-up" label="Chiffre d'affaires net (HT)" value={compact(k.chiffreAffaires)} sub="factures − retours, tous projets" />
                <Kpi icon="fa-arrow-down" label="Charges (HT)" value={compact(k.charges)} sub="achats, dépenses, main-d'œuvre" />
                <Kpi icon="fa-balance-scale" tone={k.marge < 0 ? "critical" : k.marge > 0 ? "good" : "neutral"} label="Marge"
                    value={compact(k.marge)} sub={k.tauxMarge != null ? `${k.tauxMarge} % du CA` : "pas encore de CA"} />
                <Kpi icon="fa-handshake" label="Pipeline ventes pondéré" value={compact(k.pipelinePondere)} sub={`brut ${compact(k.pipelineBrut)}`} />
            </div>

            {/* ---- Évolution + budget ---- */}
            <div className="row">
                <div className="col-lg-8 mb-3">
                    <Card title="CA et charges des projets — 6 derniers mois" icon="fa-chart-bar">
                        <Evolution data={data.evolution} />
                        <div className="small text-muted mt-2">La main-d'œuvre des tâches n'est pas datée : elle figure dans les totaux, pas dans ce graphique.</div>
                    </Card>
                </div>
                <div className="col-lg-4 mb-3">
                    <Card title="Budget des projets ouverts" icon="fa-wallet">
                        <Jauge budget={k.budgetOuverts} charges={k.chargesOuverts} engage={k.engageOuverts} />
                    </Card>
                </div>
            </div>

            {/* ---- Finances par projet ---- */}
            <div className="row">
                <div className="col-12 mb-3">
                    <Card title="Suivi financier par projet" icon="fa-coins" bodyClass="p-0"
                        right={
                            <div className="btn-group btn-group-sm">
                                {[["ouverts", "Ouverts"], ["alerte", "En alerte"], ["tous", "Tous"]].map(([v, l]) => (
                                    <button key={v} className={`btn ${financeFilter === v ? "btn-secondary" : "btn-outline-secondary"}`} onClick={() => setFinanceFilter(v)}>{l}</button>
                                ))}
                            </div>
                        }>
                        <div className="table-responsive">
                            <table className="table table-sm table-hover mb-0">
                                <thead className="thead-light">
                                    <tr>
                                        <th>Projet</th><th>Avancement</th><th className="text-right">Budget</th>
                                        <th style={{ minWidth: 160 }}>Consommation</th>
                                        <th className="text-right">CA</th><th className="text-right">Charges</th><th className="text-right">Marge</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {finances.length === 0 && <tr><td colSpan="7" className="text-center text-muted py-3">Aucun projet</td></tr>}
                                    {finances.map((p) => (
                                        <tr key={p.id}>
                                            <td>
                                                <Link to={ROUTES.fiche(p.id)}>{p.code && <span className="text-muted mr-1">{p.code}</span>}{p.nom}</Link>
                                                <div><TypeBadge type={p.type} /> <StatutBadge statut={p.statut} />
                                                    {p.enRetard && <span className="badge badge-danger ml-1">En retard</span>}</div>
                                            </td>
                                            <td style={{ minWidth: 110 }}><ProgressBar value={p.avancement} enRetard={p.enRetard} /></td>
                                            <td className="text-right text-nowrap">{p.budget != null ? compact(p.budget) : "—"}</td>
                                            <td>
                                                {p.consommation == null ? <span className="small text-muted">pas de budget</span> : (
                                                    <div title={`Charges ${formatAmount(p.charges)} + engagé ${formatAmount(p.engage)}`}>
                                                        <div className="gp-hbar">
                                                            <div className={p.horsBudget ? "gp-critical-fill" : p.consommation > 85 ? "gp-warning-fill" : "gp-c2"}
                                                                style={{ width: `${Math.min(100, p.consommation)}%` }}></div>
                                                        </div>
                                                        <div className={`small ${p.horsBudget ? "text-danger font-weight-bold" : "text-muted"}`}>
                                                            {p.horsBudget && <i className="fas fa-exclamation-triangle mr-1"></i>}{p.consommation} %
                                                        </div>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="text-right text-nowrap">{compact(p.chiffreAffaires)}</td>
                                            <td className="text-right text-nowrap">{compact(p.charges)}</td>
                                            <td className={`text-right text-nowrap font-weight-bold ${p.marge < 0 ? "text-danger" : ""}`}>
                                                {p.marge < 0 && <i className="fas fa-arrow-down mr-1"></i>}{compact(p.marge)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </div>
            </div>

            {/* ---- Répartitions + pipeline ---- */}
            <div className="row">
                <div className="col-lg-4 mb-3"><Card title="Projets par statut" icon="fa-flag"><Repartition items={data.parStatut} find={findStatut} /></Card></div>
                <div className="col-lg-4 mb-3"><Card title="Projets par type" icon="fa-shapes"><Repartition items={data.parType} find={findType} /></Card></div>
                <div className="col-lg-4 mb-3"><Card title="Pipeline des ventes" icon="fa-funnel-dollar"><Pipeline etapes={data.pipeline} /></Card></div>
            </div>

            {/* ---- Équipe + échéances ---- */}
            <div className="row">
                <div className="col-lg-6 mb-3">
                    <Card title="Charge de travail des employés" icon="fa-user-clock" bodyClass="p-0">
                        {data.chargeEmployes.length === 0 ? <div className="card-body"><Empty>Aucune tâche ouverte assignée.</Empty></div> : (
                            <table className="table table-sm mb-0">
                                <thead className="thead-light">
                                    <tr><th>Employé</th><th className="text-center">Tâches</th><th className="text-center">En retard</th><th className="text-right">Heures restantes</th><th className="text-center">Projets</th></tr>
                                </thead>
                                <tbody>
                                    {data.chargeEmployes.map((e) => (
                                        <tr key={e.employeId}>
                                            <td>{e.nom}{e.poste && <div className="small text-muted">{e.poste}</div>}</td>
                                            <td className="text-center">{e.tachesOuvertes}</td>
                                            <td className={`text-center ${e.tachesEnRetard ? "text-danger font-weight-bold" : "text-muted"}`}>{e.tachesEnRetard}</td>
                                            <td className="text-right">{e.heuresRestantes.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} h</td>
                                            <td className="text-center">{e.nbProjets}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </Card>
                </div>
                <div className="col-lg-6 mb-3">
                    <Card title="Échéances (retards et 14 prochains jours)" icon="fa-calendar-alt" bodyClass="p-0">
                        {data.echeances.length === 0 ? <div className="card-body"><Empty>Aucune échéance proche.</Empty></div> : (
                            <ul className="list-group list-group-flush">
                                {data.echeances.map((e, i) => (
                                    <li key={i} className="list-group-item py-2 d-flex align-items-center">
                                        <span className={`gp-date-chip ${e.enRetard ? "gp-date-late" : ""}`}>{formatDate(e.date)}</span>
                                        <div className="flex-grow-1 text-truncate">
                                            <i className={`fas ${e.nature === "Projet" ? "fa-flag-checkered" : "fa-tasks"} text-muted mr-1`}></i>
                                            {e.libelle}
                                            <div className="small text-muted text-truncate">
                                                <Link to={ROUTES.fiche(e.projetId)}>{e.projetNom}</Link>{e.responsable && ` · ${e.responsable}`}
                                            </div>
                                        </div>
                                        {e.enRetard && <span className="badge badge-danger ml-2">Retard</span>}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Card>
                </div>
            </div>

            {/* ---- Journal ---- */}
            <div className="row">
                <div className="col-lg-6 mb-3">
                    <Card title="Incidents ouverts" icon="fa-exclamation-triangle" bodyClass="p-0">
                        {data.incidentsOuverts.length === 0 ? <div className="card-body"><Empty>Aucun incident ouvert.</Empty></div> : (
                            <ul className="list-group list-group-flush">
                                {data.incidentsOuverts.map((a) => {
                                    const g = findGravite(a.gravite);
                                    return (
                                        <li key={a.id} className="list-group-item py-2">
                                            <div className="d-flex align-items-center">
                                                {g && <span className={`badge ${g.css} mr-2`}>{g.label}</span>}
                                                <strong className="text-truncate">{a.titre}</strong>
                                                <span className="ml-auto small text-muted text-nowrap">{formatDate(a.date)}</span>
                                            </div>
                                            <div className="small text-muted"><Link to={ROUTES.fiche(a.projetId)}>{a.projetNom}</Link>{a.auteur && ` · ${a.auteur}`}</div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </Card>
                </div>
                <div className="col-lg-6 mb-3">
                    <Card title="Activité récente du journal" icon="fa-book" bodyClass="p-0">
                        {data.activiteRecente.length === 0 ? <div className="card-body"><Empty>Le journal est vide.</Empty></div> : (
                            <ul className="list-group list-group-flush">
                                {data.activiteRecente.map((a) => {
                                    const t = findTypeJournal(a.type);
                                    return (
                                        <li key={a.id} className="list-group-item py-2 d-flex align-items-center">
                                            <span className="gp-act-icon mr-2" style={{ backgroundColor: t.color }}><i className={`fas ${t.icon}`}></i></span>
                                            <div className="flex-grow-1 text-truncate">
                                                {a.titre}
                                                <div className="small text-muted text-truncate"><Link to={ROUTES.fiche(a.projetId)}>{a.projetNom}</Link> · {t.label}{a.auteur && ` · ${a.auteur}`}</div>
                                            </div>
                                            <span className="small text-muted text-nowrap ml-2">{formatDate(a.date)}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default DashboardProjets;
